/**
 * Alpine Collection Stores v1.0.0
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Page-specific Alpine data for collection and index pages.
 * Loaded by snippets/scripts.liquid when template.name == 'collection' or 'index'.
 *
 * Data:
 * - collectionSlider: CSS scroll-snap carousel with arrow nav
 * - productGrid:      Product grid with filters, sort, infinite scroll
 *
 * WHY IIFE (not ES Module):
 * Must register data via alpine:init, which fires during Alpine's defer
 * execution. ES Modules execute after defer scripts and miss the event.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 */

(function() {
  'use strict';

  // alpine-stores.js runs first and sets window.Theme.Logger
  const Logger = window.Theme?.Logger || {
    ns: () => ({
      debug: () => {},
      info: () => {},
      warn: console.warn.bind(console),
      error: console.error.bind(console)
    })
  };

  const log = Logger.ns('collection-stores');

  const setupCollectionStores = () => {
    if (typeof Alpine === 'undefined') return;

    // ─────────────────────────────────────────────────────────────────────────────
    // Alpine.data('collectionSlider') - Horizontal collection navigation
    // ─────────────────────────────────────────────────────────────────────────────
    //
    // CSS scroll-snap carousel with arrow navigation.
    // Arrows auto-hide at scroll boundaries.
    //
    // Usage (via collection-slider.liquid):
    //   x-data="collectionSlider"
    //
    // Refs:
    //   track — the scrollable flex container
    // ─────────────────────────────────────────────────────────────────────────────

    Alpine.data('collectionSlider', () => ({
      canScrollLeft: false,
      canScrollRight: false,
      _scrollHandler: null,
      _resizeObserver: null,

      init() {
        this.$nextTick(() => {
          const track = this.$refs.track;
          if (!track) return;

          this._updateArrows();
          this._scrollHandler = () => this._updateArrows();
          track.addEventListener('scroll', this._scrollHandler, { passive: true });
          this._resizeObserver = new ResizeObserver(() => this._updateArrows());
          this._resizeObserver.observe(track);
        });
      },

      destroy() {
        const track = this.$refs.track;
        if (track && this._scrollHandler) {
          track.removeEventListener('scroll', this._scrollHandler);
        }
        if (this._resizeObserver) {
          this._resizeObserver.disconnect();
          this._resizeObserver = null;
        }
      },

      _updateArrows() {
        const el = this.$refs.track;
        if (!el) return;
        // Both arrows visible whenever content overflows (infinite wrap-around)
        const hasOverflow = el.scrollWidth > el.clientWidth + 1;
        this.canScrollLeft = hasOverflow;
        this.canScrollRight = hasOverflow;
      },

      _itemMetrics() {
        const el = this.$refs.track;
        const items = el?.querySelectorAll('.cs-item');
        if (!el || !items?.length) return null;
        const gap = parseFloat(getComputedStyle(el).gap) || 0;
        const step = items[0].offsetWidth + gap;
        const visible = Math.round(el.clientWidth / step) || 1;
        return { items, step, visible };
      },

      scrollPrev() {
        const el = this.$refs.track;
        if (!el) return;
        const m = this._itemMetrics();
        if (!m) return;

        const current = Math.round(el.scrollLeft / m.step);
        let target = current - m.visible;

        if (target < 0) {
          // Wrap to last page
          target = Math.max(0, m.items.length - m.visible);
        }
        el.scrollTo({ left: m.items[target].offsetLeft, behavior: 'smooth' });
      },

      scrollNext() {
        const el = this.$refs.track;
        if (!el) return;
        const m = this._itemMetrics();
        if (!m) return;

        const current = Math.round(el.scrollLeft / m.step);
        let target = current + m.visible;

        if (target >= m.items.length) {
          // Wrap to start
          el.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          el.scrollTo({ left: m.items[target].offsetLeft, behavior: 'smooth' });
        }
      }
    }));

    // ─────────────────────────────────────────────────────────────────────────────
    // Alpine.data('productGrid') - Product grid with filters, sort, infinite scroll
    // ─────────────────────────────────────────────────────────────────────────────
    //
    // Unified component for product grid pages. Supports:
    //   - Infinite scroll (loadMore → append)
    //   - Filter/sort/pagination (replaceGrid → full replace)
    //   - Skeleton shimmer during load
    //   - Browser history (pushState + popstate)
    //
    // Usage in template JSON:
    //   Wrap grid + UI snippets in custom-code with x-data="productGrid"
    //   Grid must have data-product-grid attribute
    //
    // Snippets:
    //   collection-filters     — Desktop dropdowns + mobile drawer
    //   collection-toolbar     — Sort dropdown + product count
    //   product-grid-infinite  — Auto-load sentinel + manual button
    //   product-grid-pagination — Numbered page links
    // ─────────────────────────────────────────────────────────────────────────────

    const gridLog = Logger.ns('product-grid');

    Alpine.data('productGrid', () => ({
      page: 1,
      loading: false,
      done: false,
      autoLoadsLeft: 3,
      totalProducts: 0,
      loadedCount: 0,
      sectionId: null,
      _abortController: null,
      _errorCount: 0,
      _replaceRequestId: 0,
      _root: null,

      init() {
        // Save root reference — $el is only reliable here in init()
        this._root = this.$el;

        // Read section ID from DOM
        const sectionEl = this._root.closest('.shopify-section');
        this.sectionId = sectionEl?.id?.replace('shopify-section-', '');

        // Read current page from URL
        const urlPage = new URLSearchParams(window.location.search).get('page');
        this.page = parseInt(urlPage) || 1;

        // Find grid and read pagination metadata
        const grid = this._root.querySelector('[data-product-grid]');
        if (grid) {
          const meta = grid.querySelector('script[data-pagination]');
          if (meta) {
            try {
              const data = JSON.parse(meta.textContent);
              this.totalProducts = data.total || 0;
            } catch (e) {}
            meta.remove();
          }
          this.loadedCount = this._countGridChildren(grid);

          if (this.totalProducts > 0 && this.loadedCount >= this.totalProducts) {
            this.done = true;
          }
        }

        // popstate: re-fetch on browser back/forward
        window.addEventListener('popstate', () => {
          this._replaceGrid(window.location.href, false);
        });

        gridLog.warn('Initialized', {
          page: this.page,
          total: this.totalProducts,
          loaded: this.loadedCount,
          done: this.done,
          sectionId: this.sectionId
        });
      },

      // ─── Getters ────────────────────────────────────────────

      get statusText() {
        if (this.totalProducts > 0) {
          return 'Załadowano ' + this.loadedCount + ' z ' + this.totalProducts + ' produktów';
        }
        return '';
      },

      get showButton() {
        return !this.done && this.autoLoadsLeft <= 0;
      },

      // ─── Infinite scroll: APPEND next page ─────────────────

      async loadMore() {
        if (this.loading || this.done) return;
        this.loading = true;
        this.page++;

        const grid = this._root.querySelector('[data-product-grid]');
        if (!grid || !this.sectionId) {
          gridLog.error('Grid or section ID not found', {
            hasGrid: !!grid,
            sectionId: this.sectionId,
            elTag: this._root?.tagName,
            elClass: this._root?.className,
            childCount: this._root?.children?.length,
            innerHTML: this._root?.innerHTML?.substring(0, 300)
          });
          this.done = true;
          this.loading = false;
          return;
        }

        try {
          const url = new URL(window.location.href);
          url.searchParams.set('page', this.page);
          url.searchParams.set('section_id', this.sectionId);

          gridLog.warn('loadMore fetch', { page: this.page, url: url.toString() });
          const html = await this._fetch(url);
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newGrid = doc.querySelector('[data-product-grid]');

          if (!newGrid) {
            gridLog.error('loadMore: [data-product-grid] not found in response', {
              responseLength: html.length,
              responsePreview: html.substring(0, 500)
            });
            this.done = true;
            return;
          }

          // Read pagination metadata from response
          this._readPagination(newGrid);

          const newChildren = Array.from(newGrid.children).filter(
            el => el.tagName !== 'SCRIPT' && el.tagName !== 'TEMPLATE'
          );

          if (newChildren.length === 0) {
            gridLog.warn('No more products — done');
            this.done = true;
            return;
          }

          // Deduplicate: skip products already in the grid (by href)
          const existingUrls = new Set(
            Array.from(grid.querySelectorAll('a[href]')).map(a => a.getAttribute('href'))
          );
          const uniqueChildren = newChildren.filter(child => {
            const link = child.querySelector ? child.querySelector('a[href]') : null;
            return !link || !existingUrls.has(link.getAttribute('href'));
          });

          if (uniqueChildren.length === 0) {
            gridLog.warn('All products duplicated — server not paginating, done');
            this.done = true;
            return;
          }

          const wasManual = this.autoLoadsLeft <= 0;
          uniqueChildren.forEach(child => grid.appendChild(child));
          this.loadedCount = this._countGridChildren(grid);

          if (wasManual) {
            this.autoLoadsLeft = 3;
          } else {
            this.autoLoadsLeft--;
          }

          // Initialize Alpine on new elements
          uniqueChildren.forEach(child => Alpine.initTree(child));

          if (this.totalProducts > 0 && this.loadedCount >= this.totalProducts) {
            this.done = true;
          }

          gridLog.warn('loadMore done', {
            page: this.page,
            added: uniqueChildren.length,
            loaded: this.loadedCount,
            autoLoadsLeft: this.autoLoadsLeft
          });

        } catch (err) {
          this._errorCount++;
          gridLog.error('loadMore failed', { error: err.message, attempt: this._errorCount });
          this.page--; // revert page increment so retry uses same page
          if (this._errorCount >= 3) {
            gridLog.error('loadMore: too many errors, giving up');
            this.done = true;
          }
        } finally {
          this.loading = false;
        }
      },

      // ─── Filter/Sort/Page: REPLACE grid ────────────────────

      applyFilters() {
        const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
        const container = this._root.querySelector(isDesktop ? '[data-filters-desktop]' : '[data-filters-mobile]');
        const inputs = container
          ? container.querySelectorAll('[data-filter-input]')
          : this._root.querySelectorAll('[data-filter-input]');

        const params = new URLSearchParams();
        inputs.forEach(input => {
          if (input.type === 'checkbox' && !input.checked) return;
          const val = input.value;
          if (val === '' || val == null) return;

          if (input.type === 'number' && (input.name.includes('.gte') || input.name.includes('.lte'))) {
            const cents = Math.round(parseFloat(val) * 100);
            if (!isNaN(cents) && cents > 0) params.append(input.name, cents.toString());
          } else {
            params.append(input.name, val);
          }
        });

        // Preserve sort
        const sortSelect = this._root.querySelector('[data-sort-select]');
        if (sortSelect?.value) params.set('sort_by', sortSelect.value);

        this._replaceGrid(window.location.pathname + '?' + params.toString(), true);
      },

      applySort(sortBy) {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', sortBy);
        url.searchParams.delete('page');
        this._replaceGrid(url.toString(), true);
      },

      removeFilter(url) {
        this._replaceGrid(url, true);
      },

      clearFilters() {
        const sortSelect = this._root.querySelector('[data-sort-select]');
        let url = window.location.pathname;
        if (sortSelect?.value) url += '?sort_by=' + sortSelect.value;
        this._replaceGrid(url, true);
      },

      goToPage(url) {
        this._replaceGrid(url, true);
      },

      // ─── Internal: replace grid + surrounding UI ───────────

      async _replaceGrid(targetUrl, pushState = true) {
        if (this._abortController) this._abortController.abort();
        this.loading = true;
        const requestId = ++this._replaceRequestId;

        try {
          const url = new URL(targetUrl, window.location.origin);
          await window.Theme.sectionRenderer.renderSection(this.sectionId, { cache: false, url });

          // Stale-request guard: a newer request already started, discard this result
          if (requestId !== this._replaceRequestId) return;

          // Read pagination metadata from the (now morphed) live DOM
          this._readPagination();

          // Reset state for new result set
          this.page = parseInt(url.searchParams.get('page')) || 1;
          this.done = false;
          this.autoLoadsLeft = 3;
          const grid = this._root.querySelector('[data-product-grid]');
          if (grid) {
            this.loadedCount = this._countGridChildren(grid);
            if (this.totalProducts > 0 && this.loadedCount >= this.totalProducts) {
              this.done = true;
            }
          }

          if (pushState) window.history.pushState({}, '', targetUrl);
          this._root.scrollIntoView({ behavior: 'smooth', block: 'start' });

          gridLog.warn('Grid replaced', {
            url: targetUrl,
            loaded: this.loadedCount,
            total: this.totalProducts
          });

        } catch (err) {
          if (requestId !== this._replaceRequestId) return;
          gridLog.error('_replaceGrid failed', { error: err.message });
          window.location.href = targetUrl;
        } finally {
          if (requestId === this._replaceRequestId) this.loading = false;
        }
      },

      _readPagination(gridEl) {
        if (!gridEl) gridEl = this._root.querySelector('[data-product-grid]');
        if (!gridEl) return;
        const meta = gridEl.querySelector('script[data-pagination]');
        if (meta) {
          try {
            const data = JSON.parse(meta.textContent);
            this.totalProducts = data.total || this.totalProducts;
            gridLog.warn('Pagination metadata found', data);
          } catch (e) {
            gridLog.error('Pagination metadata parse error', { text: meta.textContent });
          }
          meta.remove();
        } else {
          gridLog.warn('No pagination metadata in grid');
        }
      },

      _countGridChildren(grid) {
        return Array.from(grid.children).filter(
          el => el.tagName !== 'SCRIPT' && el.tagName !== 'TEMPLATE'
        ).length;
      },

      async _fetch(url) {
        this._abortController = new AbortController();
        const res = await fetch(url, { signal: this._abortController.signal });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      }
    }));

    log.info('Alpine collection stores registered');
  };

  document.addEventListener('alpine:init', setupCollectionStores);

  log.info('Alpine collection stores v1.0.0 loaded');
})();
